
import { Category, Question } from '@/types/assessment';
import { useToast } from '@/hooks/use-toast';

interface QuestionHandlersProps {
  categories: Category[];
  setCategories: React.Dispatch<React.SetStateAction<Category[]>>;
  selectedCategory: Category | undefined;
  setIsAddingQuestion: (value: boolean) => void;
  setEditingQuestionId: (id: string | null) => void;
  onContentChange?: () => void; // Add callback for content changes
}

export function useQuestionHandlers({
  categories,
  setCategories,
  selectedCategory,
  setIsAddingQuestion,
  setEditingQuestionId,
  onContentChange
}: QuestionHandlersProps) {
  const { toast } = useToast();

  // Handler for adding a new question
  const handleAddQuestion = () => {
    if (!selectedCategory) {
      toast({
        title: "No category selected",
        description: "Please select a category first.",
        variant: "destructive"
      });
      return;
    }
    
    setIsAddingQuestion(true);
    setEditingQuestionId(null);
  };

  // Handler for editing an existing question
  const handleEditQuestion = (questionId: string) => {
    setEditingQuestionId(questionId);
    setIsAddingQuestion(false);
  };

  // Handler for closing question form
  const handleCloseQuestionForm = () => {
    setIsAddingQuestion(false);
    setEditingQuestionId(null);
  };

  // Handler for saving a question (both new and edited)
  // NOTE: Questions are saved locally in state. They are only persisted to database
  // when the user explicitly clicks "Update" or "Save Draft" button.
  const handleSaveQuestion = (categoryId: string, questionData: Partial<Question>) => {
    if (!questionData.text?.trim()) {
      toast({
        title: "Invalid question",
        description: "Question text is required.",
        variant: "destructive"
      });
      return;
    }

    setCategories(prevCategories => 
      prevCategories.map(category => {
        if (category.id === categoryId) {
          if (questionData.id) {
            // Update existing question
            return {
              ...category,
              questions: category.questions.map(question =>
                question.id === questionData.id
                  ? { ...question, ...questionData } as Question
                  : question
              )
            };
          } else {
            // Add new question
            const newQuestion: Question = {
              id: crypto.randomUUID(),
              text: questionData.text,
              explanation: questionData.explanation,
              answerType: questionData.answerType || 'single',
              answers: questionData.answers || [
                { id: crypto.randomUUID(), text: 'Yes', value: 1 },
                { id: crypto.randomUUID(), text: 'No', value: 0 }
              ],
              required: questionData.required ?? true,
              premium: questionData.premium ?? false,
              disabled: questionData.disabled ?? false
            };

            return {
              ...category,
              questions: [...category.questions, newQuestion]
            };
          }
        }
        return category;
      })
    );

    // Close the form
    setIsAddingQuestion(false);
    setEditingQuestionId(null);

    // DO NOT call onContentChange here - this was triggering the version dialog
    // Questions are accumulated locally and saved only when user clicks "Update"

    toast({
      title: "Success",
      description: questionData.id
        ? "Question updated locally. Click Update/Save Draft to persist your changes."
        : "Question added locally. Click Update/Save Draft to persist your changes."
    });
  };

  // Handler for deleting a question
  // NOTE: Deletion is local only until user clicks "Update"
  const handleDeleteQuestion = (categoryId: string, questionId: string) => {
    setCategories(prevCategories => 
      prevCategories.map(category => 
        category.id === categoryId 
          ? {
              ...category,
              questions: category.questions.filter(question => question.id !== questionId)
            }
          : category
      )
    );

    // DO NOT call onContentChange - changes saved on explicit "Update" click

    toast({
      title: "Question deleted",
      description: "Click Update/Save Draft to persist your changes."
    });
  };

  // Handler for toggling question disabled status
  const handleToggleQuestionStatus = (categoryId: string, questionId: string) => {
    setCategories(prevCategories => 
      prevCategories.map(category => 
        category.id === categoryId 
          ? {
              ...category,
              questions: category.questions.map(question => 
                question.id === questionId 
                  ? { ...question, disabled: !question.disabled } 
                  : question
              )
            }
          : category
      )
    );

    // DO NOT call onContentChange - changes saved on explicit "Update" click

    const question = categories
      .find(cat => cat.id === categoryId)
      ?.questions.find(q => q.id === questionId);

    toast({
      title: question?.disabled ? "Question enabled" : "Question disabled",
      description: "Click Update/Save Draft to persist your changes."
    });
  };

  // Handler for drag and drop reordering
  const handleDragEnd = (result: any) => {
    if (!result.destination || !selectedCategory) {
      return;
    }

    const items = Array.from(selectedCategory.questions);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setCategories(prevCategories =>
      prevCategories.map(category =>
        category.id === selectedCategory.id
          ? { ...category, questions: items }
          : category
      )
    );

    // DO NOT call onContentChange - changes saved on explicit "Update" click
  };

  // Handler for updating a question
  const handleUpdateQuestion = (categoryId: string, questionId: string, updates: any) => {
    setCategories(prevCategories => 
      prevCategories.map(category => 
        category.id === categoryId 
          ? {
              ...category,
              questions: category.questions.map(question => 
                question.id === questionId 
                  ? { ...question, ...updates } 
                  : question
              )
            }
          : category
      )
    );

    // DO NOT call onContentChange - changes saved on explicit "Update" click
  };

  // Handler for updating an answer
  const handleUpdateAnswer = (categoryId: string, questionId: string, answerId: string, updates: any) => {
    setCategories(prevCategories => 
      prevCategories.map(category => 
        category.id === categoryId 
          ? {
              ...category,
              questions: category.questions.map(question => 
                question.id === questionId 
                  ? {
                      ...question,
                      answers: question.answers.map(answer => 
                        answer.id === answerId 
                          ? { ...answer, ...updates } 
                          : answer
                      )
                    } 
                  : question
              )
            }
          : category
      )
    );

    // DO NOT call onContentChange - changes saved on explicit "Update" click
  };

  // Handler for adding an answer
  const handleAddAnswer = (categoryId: string, questionId: string) => {
    const newAnswer = {
      id: crypto.randomUUID(),
      text: 'New Answer',
      value: 0
    };

    setCategories(prevCategories => 
      prevCategories.map(category => 
        category.id === categoryId 
          ? {
              ...category,
              questions: category.questions.map(question => 
                question.id === questionId 
                  ? {
                      ...question,
                      answers: [...question.answers, newAnswer]
                    } 
                  : question
              )
            }
          : category
      )
    );

    // DO NOT call onContentChange - changes saved on explicit "Update" click
  };

  return {
    handleAddQuestion,
    handleEditQuestion,
    handleCloseQuestionForm,
    handleSaveQuestion,
    handleDeleteQuestion,
    handleToggleQuestionStatus,
    handleDragEnd,
    handleUpdateQuestion,
    handleUpdateAnswer,
    handleAddAnswer
  };
}
